/**
 * Scroll-scrubbed video + text: one GSAP timeline, one ScrollTrigger (scrub, no pin).
 * GSAP scripts must load before this file (see scrub-video-scroll.liquid).
 */
(function () {
  "use strict";

  if (window.__HAVA_SCRUB_VIDEO_SCROLL__) return;
  window.__HAVA_SCRUB_VIDEO_SCROLL__ = true;

  var SCRIPT_NAME = "scrub-video-scroll";

  /** Coarse pointers (typical phones/tablets): heavy currentTime churn blanks frames on iOS/WebKit. */
  var prefersCoarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  var scrollTriggerMobileConfigured = false;

  function configureScrollTriggerForMobile(ScrollTrigger) {
    if (!prefersCoarsePointer || scrollTriggerMobileConfigured) return;
    scrollTriggerMobileConfigured = true;
    try {
      // Stops reflow/jump when the address bar toggles (window.innerHeight oscillation).
      ScrollTrigger.config({ ignoreMobileResize: true });
    } catch (e) {}
    /* iOS/WebKit: ties scroll position to the compositor — fewer micro “bounce” oscillations vs pin. */
    if (typeof ScrollTrigger.normalizeScroll === "function") {
      try {
        ScrollTrigger.normalizeScroll({
          allowNestedScroll: true,
        });
      } catch (e) {}
    }
  }

  function bindVideoScrub(tl, video, state, timelineDuration) {
    var dur = timelineDuration;
    if (!isFinite(dur) || dur <= 0) dur = 1;

    var rafId = null;
    var pendingTime = null;
    /** Skip micro-seeks on touch devices — decoder keeps up better with a slightly looser tolerance. */
    var minSeekDelta = prefersCoarsePointer ? 0.04 : 0.001;

    function applySeek() {
      rafId = null;
      if (pendingTime == null) return;
      var t = pendingTime;
      pendingTime = null;
      var d = video.duration;
      if (!d || !isFinite(d)) return;
      try {
        video.currentTime = Math.min(Math.max(t, 0), d - 0.001);
      } catch (e) {}
    }

    tl.to(
      state,
      {
        progress: 1,
        duration: dur,
        ease: "none",
        immediateRender: false,
        onUpdate: function () {
          var d = video.duration;
          if (!d || !isFinite(d)) return;
          var t = state.progress * d;
          if (Math.abs(video.currentTime - t) > minSeekDelta) {
            pendingTime = t;
            if (!rafId) rafId = requestAnimationFrame(applySeek);
          }
        },
      },
      0,
    );
  }

  /**
   * Mobile WebKit often won't paint scrubbed frames until the element has entered
   * playback once (muted + playsinline). Prime without audible output.
   */
  function primeVideoForScrubbing(video) {
    if (!video || video.dataset.scrubPrimed === "1") return;
    function tryPrime() {
      if (video.dataset.scrubPrimed === "1") return;
      var p = video.play();
      if (p && typeof p.then === "function") {
        p.then(function () {
          try {
            video.pause();
          } catch (e) {}
          video.dataset.scrubPrimed = "1";
        }).catch(function () {
          video.dataset.scrubPrimed = "1";
        });
      } else {
        video.dataset.scrubPrimed = "1";
      }
    }
    if (video.readyState >= 1) {
      tryPrime();
    } else {
      video.addEventListener("loadedmetadata", tryPrime, { once: true });
    }
  }

  function getScrubVideoEl(section) {
    return section.querySelector("[data-scrub-video]");
  }

  function getSplitMediaConfig(section) {
    var el = section.querySelector("[data-scrub-split-media]");
    if (!el) return null;
    var raw = (el.textContent || "").trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * Split desktop/mobile: inject <source> nodes for the current breakpoint only (no <source media> — WebKit/iOS issues).
   */
  function hydrateSplitScrubVideo(section, video, force) {
    if (!video || !video.hasAttribute("data-scrub-responsive-sources")) return;
    var cfg = getSplitMediaConfig(section);
    if (!cfg) return;
    var narrow =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 749px)").matches;
    var bucket = narrow ? "mobile" : "desktop";
    if (!force && video.dataset.scrubHydratedBucket === bucket) return;

    var list = cfg[bucket];
    if (!list || !list.length) return;

    while (video.firstChild) {
      video.removeChild(video.firstChild);
    }
    video.removeAttribute("src");
    for (var i = 0; i < list.length; i++) {
      var row = list[i];
      if (!row || !row.url) continue;
      var srcEl = document.createElement("source");
      srcEl.src = row.url;
      if (row.type) srcEl.type = row.type;
      video.appendChild(srcEl);
    }
    video.dataset.scrubHydratedBucket = bucket;
    try {
      delete video.dataset.scrubPrimed;
    } catch (e) {}
    try {
      video.load();
    } catch (err) {}
  }

  /** Set poster to match breakpoint (native poster is singular). */
  function applyResponsiveVideoPoster(video) {
    if (!video || !video.hasAttribute("data-scrub-responsive-sources")) return;
    var d = video.getAttribute("data-scrub-poster-desktop") || "";
    var m = video.getAttribute("data-scrub-poster-mobile") || "";
    if (!d && !m) return;
    var narrow = window.matchMedia("(max-width: 749px)").matches;
    var url = narrow ? m || d : d || m;
    if (url) video.setAttribute("poster", url);
  }

  /**
   * Lazy sections use preload=metadata until nearing the viewport; then switch to auto + load().
   */
  function boostSectionVideoPreload(section) {
    var videos = section.querySelectorAll("video");
    if (!videos.length) return;
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      try {
        delete v.dataset.scrubPrimed;
      } catch (e) {}
      try {
        v.preload = "auto";
        v.load();
      } catch (err) {}
      primeVideoForScrubbing(v);
    }
  }

  function wireLazyVideoPreload(section) {
    if (!section || section.getAttribute("data-scrub-lazy-preload") !== "true") {
      return;
    }

    function boost() {
      if (section._scrubLazyObs) {
        try {
          section._scrubLazyObs.disconnect();
        } catch (e) {}
        section._scrubLazyObs = null;
      }
      boostSectionVideoPreload(section);
    }

    if (typeof IntersectionObserver !== "function") {
      boost();
      return;
    }

    var obs = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            boost();
            break;
          }
        }
      },
      { root: null, rootMargin: "0px 0px 25% 0px", threshold: 0 },
    );
    section._scrubLazyObs = obs;
    obs.observe(section);
  }

  /** Optional <img> cover until first decoded frame; scrub still runs underneath. */
  function wirePosterLayer(video, poster) {
    if (!video || !poster) return;

    var revealed = false;
    /** Longer on coarse pointers: decode/first-frame often arrives after 8s on cellular. */
    var posterTimeoutMs = prefersCoarsePointer ? 20000 : 8000;
    var tmo = window.setTimeout(function onPosterTimeout() {
      /* If still no frame data, keep poster — hiding shows black under a stalled decoder. */
      if (video.readyState < 2) return;
      finish();
    }, posterTimeoutMs);

    function finish() {
      if (revealed) return;
      revealed = true;
      window.clearTimeout(tmo);
      poster.classList.add("is-hidden");
      /* Avoid ScrollTrigger.refresh() here — poster is position:absolute; refresh mid-scrub causes visible nudges. */
    }

    video.addEventListener("error", finish, { once: true });

    var started = false;
    function startReveal() {
      if (started || video.readyState < 2) return;
      started = true;
      if (typeof video.requestVideoFrameCallback === "function") {
        video.requestVideoFrameCallback(finish);
        return;
      }
      function onSeeked() {
        video.removeEventListener("seeked", onSeeked);
        finish();
      }
      video.addEventListener("seeked", onSeeked);
      try {
        var vd = video.duration;
        var bump = 0.04;
        if (vd && isFinite(vd)) {
          bump = Math.min(0.04, Math.max(vd / 100, 0.001));
        }
        if (Math.abs(video.currentTime - bump) < 0.0005) {
          bump = Math.min(0.08, (vd && isFinite(vd) ? vd : 1) * 0.05);
        }
        video.currentTime = bump;
      } catch (e) {
        finish();
      }
    }

    if (video.readyState >= 2) {
      startReveal();
    } else {
      video.addEventListener("loadeddata", startReveal);
      video.addEventListener("canplay", startReveal);
    }
  }

  function wirePosterLayers(section) {
    var posters = section.querySelectorAll("[data-scrub-poster-layer]");
    if (!posters.length) return;

    var v = section.querySelector("[data-scrub-video]");
    posters.forEach(function (poster) {
      wirePosterLayer(v, poster);
    });
  }

  /**
   * Rich-text headings: italic (<em>) wraps numeric values to count 0 → target while the heading fades in.
   * Supports optional prefix/suffix (e.g. "$100", "99%") and decimals; thousands spaces/commas in the source are normalized.
   */
  function parseEmCounterPayload(text) {
    var t = String(text == null ? "" : text).trim();
    if (!t) return null;
    var m = t.match(/^([\s\S]*?)([+\-]?(?:\d[\d\s,]*)(?:\.\d+)?|[+\-]?\.\d+)([\s\S]*)$/);
    if (!m) return null;
    var mid = m[2].replace(/\s/g, "").replace(/,/g, "");
    var value = parseFloat(mid, 10);
    if (!isFinite(value)) return null;
    var decimals = 0;
    var dot = mid.indexOf(".");
    if (dot >= 0) decimals = mid.length - dot - 1;
    return { value: value, prefix: m[1], suffix: m[3], decimals: decimals };
  }

  function formatCounterValue(v, decimals) {
    if (decimals > 0) return v.toFixed(decimals);
    return String(Math.round(v));
  }

  function applyCounterToEm(el, payload, v) {
    el.textContent =
      payload.prefix + formatCounterValue(v, payload.decimals) + payload.suffix;
  }

  function prepareHeadingCounters(heading) {
    var ems = heading.querySelectorAll("em");
    var list = [];
    for (var i = 0; i < ems.length; i++) {
      var payload = parseEmCounterPayload(ems[i].textContent);
      if (!payload) continue;
      applyCounterToEm(ems[i], payload, 0);
      list.push({ el: ems[i], payload: payload });
    }
    return list;
  }

  function addHeadingCounterTweens(gsap, tl, counterList, start, duration) {
    if (!counterList.length || !gsap) return;
    var state = {};
    var fromVars = {};
    var toVars = {
      duration: duration,
      ease: "power2.out",
      onUpdate: function () {
        for (var i = 0; i < counterList.length; i++) {
          applyCounterToEm(
            counterList[i].el,
            counterList[i].payload,
            state["n" + i],
          );
        }
      },
    };
    for (var j = 0; j < counterList.length; j++) {
      var k = "n" + j;
      state[k] = 0;
      fromVars[k] = 0;
      toVars[k] = counterList[j].payload.value;
    }
    tl.fromTo(state, fromVars, toVars, start);
  }

  function addTextSteps(tl, textEls, gsap) {
    var n = textEls.length;
    if (!n) return;

    var seg = 1 / n;
    var fadeInDur = Math.max(seg * 0.22, 0.05);
    var fadeOutDur = Math.max(seg * 0.18, 0.04);
    var pad = seg * 0.08;

    textEls.forEach(function (el, i) {
      var segStart = i * seg;
      var segEnd = (i + 1) * seg;
      var delayPct = parseFloat(el.getAttribute("data-appear-delay") || "0");
      if (!isFinite(delayPct) || delayPct < 0) delayPct = 0;
      delayPct = Math.min(delayPct, 85);
      var extraInDelay = seg * (delayPct / 100);
      var inStart = segStart + pad + extraInDelay;
      var outStart = segEnd - fadeOutDur - pad;

      if (outStart <= inStart + fadeInDur * 0.5) {
        outStart = inStart + fadeInDur + 0.02;
      }

      var badgeEl = el.querySelector(".scrub-video-scroll__badge");
      var heading = el.querySelector(".scrub-video-scroll__heading");
      var counterList = heading ? prepareHeadingCounters(heading) : [];
      var subheadingEl = el.querySelector(".scrub-video-scroll__subheading");
      var sub = el.querySelector(".scrub-video-scroll__subtext");

      var subDelayPct = parseFloat(
        el.getAttribute("data-subheading-delay") || "15",
      );
      if (!isFinite(subDelayPct) || subDelayPct < 0) subDelayPct = 0;
      var subExtra =
        subDelayPct > 0 ? seg * (Math.min(subDelayPct, 80) / 100) : 0;

      function clampFadeInStart(t) {
        var maxT = outStart - fadeInDur - 0.01;
        if (t > maxT) return Math.max(inStart, maxT);
        return t;
      }

      /* Badge + heading + subheading share one fade-in phase; body follows after stagger. */
      var tTitle = inStart;
      var tBadge = badgeEl ? tTitle : null;
      var tHeading = heading ? tTitle : null;
      var tSubheading = subheadingEl ? tTitle : null;
      var hasTitle = Boolean(badgeEl || heading || subheadingEl);
      var tBody = null;
      if (sub) {
        tBody = hasTitle ? clampFadeInStart(inStart + subExtra) : inStart;
      }

      function fadeInLine(target, position) {
        if (!target) return;
        tl.fromTo(
          target,
          { autoAlpha: 0, y: 40 },
          {
            autoAlpha: 1,
            y: 0,
            duration: fadeInDur,
            ease: "power2.out",
          },
          position,
        );
      }

      function fadeOutLine(target, position) {
        if (!target) return;
        tl.to(
          target,
          {
            autoAlpha: 0,
            y: -32,
            duration: fadeOutDur,
            ease: "power2.in",
          },
          position,
        );
      }

      if (tBadge != null) fadeInLine(badgeEl, tBadge);
      if (tHeading != null) fadeInLine(heading, tHeading);
      if (counterList.length && tHeading != null) {
        addHeadingCounterTweens(gsap, tl, counterList, tHeading, fadeInDur);
      }
      if (tSubheading != null) fadeInLine(subheadingEl, tSubheading);
      if (tBody != null) fadeInLine(sub, tBody);

      var anyLine = badgeEl || heading || subheadingEl || sub;
      if (i !== n - 1 && anyLine) {
        if (badgeEl) fadeOutLine(badgeEl, outStart);
        if (heading) fadeOutLine(heading, outStart);
        if (subheadingEl) fadeOutLine(subheadingEl, outStart);
        if (sub) fadeOutLine(sub, outStart);
      }
    });
  }

  function buildTimeline(section, markers) {
    var video = getScrubVideoEl(section);
    var pinEl = section.querySelector("[data-scrub-pin]");
    var textEls = Array.prototype.slice.call(
      section.querySelectorAll("[data-scrub-text]"),
    );

    if (!video || !pinEl) return null;

    hydrateSplitScrubVideo(section, video, Boolean(section._scrubBreakpointRefresh));
    if (section._scrubBreakpointRefresh) {
      section._scrubBreakpointRefresh = false;
    }
    applyResponsiveVideoPoster(video);

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    configureScrollTriggerForMobile(ScrollTrigger);

    var videoState = { progress: 0 };
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        /* No pin: page scroll never stops; scrub tracks progress as the tall section crosses the viewport. */
        start: "top bottom",
        end: "bottom bottom",
        scrub: prefersCoarsePointer ? 0.85 : true,
        fastScrollEnd: Boolean(prefersCoarsePointer),
        markers: Boolean(markers),
      },
    });

    primeVideoForScrubbing(video);
    addTextSteps(tl, textEls, gsap);
    var scrubDuration = Math.max(tl.duration(), 1);
    bindVideoScrub(tl, video, videoState, scrubDuration);
    wirePosterLayers(section);

    return tl;
  }

  function initSection(section) {
    if (!section || section.getAttribute("data-scrub-initialized") === "1")
      return;
    var scrubVideoEl = section.querySelector("[data-scrub-video]");
    if (!scrubVideoEl) return;
    if (section._scrubInitStarted) return;
    section._scrubInitStarted = true;

    if (!window.gsap || !window.ScrollTrigger) {
      console.warn(
        "[" +
          SCRIPT_NAME +
          "] Add gsap.min.js and gsap-ScrollTrigger.min.js before this file.",
      );
      section._scrubInitStarted = false;
      return;
    }

    try {
      var markers = section.getAttribute("data-scrub-markers") === "true";
      var ctx = window.gsap.context(function () {
        buildTimeline(section, markers);
      }, section);
      section._scrubGsapContext = ctx;
      section.setAttribute("data-scrub-initialized", "1");
      wireLazyVideoPreload(section);
      if (scrubVideoEl.hasAttribute("data-scrub-responsive-sources")) {
        var mq = window.matchMedia("(max-width: 749px)");
        var onBp = function () {
          section._scrubBreakpointRefresh = true;
          destroySection(section);
          initSection(section);
        };
        mq.addEventListener("change", onBp);
        section._scrubBreakMq = mq;
        section._scrubBreakHandler = onBp;
      }
    } catch (err) {
      console.warn("[" + SCRIPT_NAME + "]", err);
      section._scrubInitStarted = false;
    }
  }

  function destroySection(section) {
    if (!section) return;
    if (section._scrubLazyObs) {
      try {
        section._scrubLazyObs.disconnect();
      } catch (e) {}
      section._scrubLazyObs = null;
    }
    if (section._scrubBreakMq && section._scrubBreakHandler) {
      section._scrubBreakMq.removeEventListener(
        "change",
        section._scrubBreakHandler,
      );
      section._scrubBreakMq = null;
      section._scrubBreakHandler = null;
    }
    section._scrubInitStarted = false;
    var ctx = section._scrubGsapContext;
    if (ctx) {
      ctx.revert();
      section._scrubGsapContext = null;
    }
    section.removeAttribute("data-scrub-initialized");
  }

  function scan(root) {
    var scope = root || document;
    scope.querySelectorAll("[data-scrub-section]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", function (e) {
    var target = e.target;
    if (!target || !target.querySelectorAll) return;
    scan(target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var inner = null;
    if (e.target && e.target.querySelector) {
      inner = e.target.querySelector("[data-scrub-section]");
    }
    if (!inner && e.detail && e.detail.sectionId) {
      var sec = document.getElementById(
        "shopify-section-" + e.detail.sectionId,
      );
      if (sec) inner = sec.querySelector("[data-scrub-section]");
    }
    if (inner) destroySection(inner);
  });
})();
